import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { timeAgo } from "@/lib/format";
import {
  BellRinging, Warning, Star, ChatCircleDots, EnvelopeSimple, DeviceMobile,
  CheckCircle, Plus, Trash, Globe, Check,
} from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const sourceMeta = {
  review: { icon: Star, color: "text-amber-600 bg-amber-50" },
  survey: { icon: Star, color: "text-primary bg-primary/10" },
  chat: { icon: ChatCircleDots, color: "text-blue-600 bg-blue-50" },
  sms: { icon: DeviceMobile, color: "text-violet-600 bg-violet-50" },
  email: { icon: EnvelopeSimple, color: "text-cyan-600 bg-cyan-50" },
};
const sevColor = { high: "border-l-destructive", medium: "border-l-amber-500", low: "border-l-muted-foreground" };
const channelIcon = { in_app: BellRinging, email: EnvelopeSimple, sms: DeviceMobile };
const channelLabel = { in_app: "In-app", email: "Email", sms: "SMS" };

export default function Alerts() {
  const qc = useQueryClient();
  const { data: feed } = useQuery({ queryKey: ["alerts"], queryFn: async () => (await api.get("/alerts")).data, refetchInterval: 12000 });
  const { data: subs = [] } = useQuery({ queryKey: ["alert-subs"], queryFn: async () => (await api.get("/alert-subscriptions")).data });
  const { data: events = {} } = useQuery({ queryKey: ["alert-events"], queryFn: async () => (await api.get("/alert-events")).data });

  const alerts = feed?.alerts || [];
  const unread = feed?.unread || 0;

  const readOne = useMutation({ mutationFn: async (id) => (await api.post(`/alerts/${id}/read`)).data, onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }) });
  const readAll = useMutation({ mutationFn: async () => (await api.post("/alerts/read-all")).data, onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts"] }); toast.success("All alerts marked read"); } });

  return (
    <Page>
      <PageHeader title="Alerts" subtitle="Get notified the moment a guest has a negative experience — and manage what you subscribe to.">
        {unread > 0 && <span className="text-[11px] px-2.5 py-1 rounded-full bg-destructive/10 text-destructive border border-destructive/20 font-medium">{unread} unread</span>}
      </PageHeader>

      <Tabs defaultValue="feed">
        <TabsList className="mb-5">
          <TabsTrigger value="feed" data-testid="tab-alert-feed">Alert feed</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-alert-rules">My subscriptions</TabsTrigger>
        </TabsList>

        <TabsContent value="feed">
          <div className="flex justify-end mb-3">
            <Button data-testid="mark-all-read-btn" variant="outline" size="sm" className="gap-1.5" disabled={!unread || readAll.isPending} onClick={() => readAll.mutate()}><CheckCircle size={15} /> Mark all read</Button>
          </div>
          <div className="space-y-2.5">
            {alerts.length === 0 && <div className="text-center text-muted-foreground py-12 rounded-lg border border-dashed border-border">No alerts. You're all caught up 🎉</div>}
            {alerts.map((a, i) => {
              const sm = sourceMeta[a.source] || sourceMeta.review;
              return (
                <div key={a.id} data-testid={`alert-row-${i}`} className={`fade-up rounded-lg border border-border border-l-[3px] ${sevColor[a.severity] || sevColor.low} bg-card p-4 flex items-start gap-3.5 ${a.read ? "opacity-70" : ""}`} style={{ animationDelay: `${i * 40}ms` }}>
                  <span className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${sm.color}`}><sm.icon size={17} weight="fill" /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{a.title}</span>
                      {!a.read && <span className="h-2 w-2 rounded-full bg-destructive pulse-dot" />}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border capitalize ${a.severity === "high" ? "border-destructive/20 bg-destructive/10 text-destructive" : "border-amber-200 bg-amber-50 text-amber-600"}`}>{a.severity}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{a.detail}</p>
                    <div className="text-xs text-muted-foreground mt-1.5">{a.guest_name} · via {a.source} · {timeAgo(a.created_at)}</div>
                  </div>
                  {!a.read && <Button data-testid={`alert-read-${i}`} variant="ghost" size="sm" className="h-8 gap-1 text-muted-foreground shrink-0" onClick={() => readOne.mutate(a.id)}><Check size={14} /> Read</Button>}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="rules">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">Choose which events alert you, and how you're notified.</p>
            <NewRuleDialog events={events} qc={qc} />
          </div>
          <div className="space-y-2.5">
            {subs.map((s, i) => <RuleCard key={s.id} s={s} i={i} events={events} qc={qc} />)}
          </div>
        </TabsContent>
      </Tabs>
    </Page>
  );
}

function RuleCard({ s, i, events, qc }) {
  const toggle = useMutation({ mutationFn: async () => (await api.post(`/alert-subscriptions/${s.id}/toggle`)).data, onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["alert-subs"] }); toast.success(d.active ? "Subscription enabled" : "Subscription paused"); } });
  const del = useMutation({ mutationFn: async () => (await api.delete(`/alert-subscriptions/${s.id}`)).data, onSuccess: () => { qc.invalidateQueries({ queryKey: ["alert-subs"] }); toast.success("Subscription removed"); } });
  return (
    <div data-testid={`alert-rule-${i}`} className="fade-up rounded-lg border border-border bg-card p-4 flex items-center gap-4" style={{ animationDelay: `${i * 40}ms` }}>
      <span className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${s.active ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}><BellRinging size={20} weight="fill" /></span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{s.name}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${s.active ? "bg-success/10 text-success border-success/20" : "bg-secondary text-muted-foreground border-border"}`}>{s.active ? "Active" : "Paused"}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{events[s.event_type] || s.event_type}</div>
        <div className="flex items-center gap-1.5 mt-2">
          {(s.channels || []).map((c) => {
            const CI = channelIcon[c] || Globe;
            return <span key={c} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-secondary border border-border"><CI size={11} weight="fill" /> {channelLabel[c] || c}</span>;
          })}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Switch data-testid={`alert-rule-toggle-${i}`} checked={s.active} onCheckedChange={() => toggle.mutate()} />
        <Button data-testid={`alert-rule-delete-${i}`} variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive" onClick={() => del.mutate()}><Trash size={15} /></Button>
      </div>
    </div>
  );
}

function NewRuleDialog({ events, qc }) {
  const [open, setOpen] = useState(false);
  const eventKeys = Object.keys(events);
  const [name, setName] = useState("");
  const [eventType, setEventType] = useState("");
  const [channels, setChannels] = useState(["in_app"]);

  const toggleChannel = (c) => setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  const valid = name.trim() && eventType && channels.length;

  const create = useMutation({
    mutationFn: async () => (await api.post("/alert-subscriptions", { name, event_type: eventType, channels, active: true })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alert-subs"] }); toast.success("Alert rule created"); setOpen(false); setName(""); setEventType(""); setChannels(["in_app"]); },
    onError: () => toast.error("Could not create rule"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-alert-rule-btn" className="gap-1.5"><Plus size={16} weight="bold" /> New alert rule</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Subscribe to an alert</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div>
            <label className="text-[13px] font-medium mb-1.5 block">Name</label>
            <input data-testid="rule-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. VIP negative feedback" className="w-full h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="text-[13px] font-medium mb-1.5 block">Alert me when…</label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger data-testid="rule-event"><SelectValue placeholder="Choose an event" /></SelectTrigger>
              <SelectContent>{eventKeys.map((k) => <SelectItem key={k} value={k}>{events[k]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[13px] font-medium mb-1.5 block">Notify via</label>
            <div className="flex gap-2">
              {["in_app", "email", "sms"].map((c) => {
                const CI = channelIcon[c];
                const on = channels.includes(c);
                return (
                  <button key={c} type="button" data-testid={`rule-channel-${c}`} onClick={() => toggleChannel(c)}
                    className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-md border text-sm transition-colors ${on ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                    <CI size={15} weight="fill" /> {channelLabel[c]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="save-rule-btn" disabled={!valid || create.isPending} onClick={() => create.mutate()}>{create.isPending ? "Saving…" : "Create rule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
